import React from 'react';
import { Languages, ChefHat, Wine, Beer, Palette, Check } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, Kicker } from '../components/Reveal';
import { SEO } from '../components/SEO';
import { IMG, Banner } from './Home';

const icons = [ChefHat, Wine, Beer, Palette];

export default function TeamBuilding() {
  const { t } = useLanguage();

  return (
    <div data-testid="team-building-page">
      <SEO title={t.meta.tbTitle} description={t.meta.tbDesc} />

      <section className="relative pt-40 pb-24 md:pb-32 bg-emerald-deep grain overflow-hidden" data-testid="tb-hero">
        <div className="absolute inset-0 opacity-20">
          <img src={IMG.cooking} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-emerald-deep/60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <Kicker light>{t.tb.kicker}</Kicker>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">{t.tb.title}</h1>
            <p className="mt-6 max-w-2xl text-ivory/70 text-base md:text-lg leading-relaxed">{t.tb.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="tb-formats">
        <Reveal>
          <Kicker>{t.tb.formatsKicker}</Kicker>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.tb.formats.map((f, i) => {
            const Icon = icons[i];
            return (
              <Reveal key={f.title} delay={i * 0.08}>
                <article
                  data-testid={`tb-format-${i}`}
                  className="h-full bg-white border border-emerald/10 rounded-sm p-8 transition-all duration-500 hover:shadow-[0_12px_50px_rgba(37,79,87,0.14)] hover:-translate-y-1"
                >
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-ivory text-terracotta-dark">
                    <Icon size={22} strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-6 font-serif text-2xl text-emerald">{f.title}</h2>
                  <p className="mt-3 text-sm text-emerald-ink/60 leading-relaxed">{f.desc}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-ivory-soft border-y border-emerald/10" data-testid="tb-packages">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Kicker>{t.tb.packagesKicker}</Kicker>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-emerald tracking-tight max-w-2xl">{t.tb.packagesTitle}</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full bg-white border border-emerald/10 rounded-sm p-8 md:p-10" data-testid="tb-includes-card">
                <h3 className="font-serif text-2xl text-emerald">{t.tb.includeTitle}</h3>
                <ul className="mt-6 space-y-4">
                  {t.tb.includes.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <Check size={18} className="text-terracotta-dark shrink-0 mt-0.5" strokeWidth={2} />
                      <span className="text-sm md:text-base text-emerald-ink/70 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full bg-white border border-emerald/10 rounded-sm p-8 md:p-10" data-testid="tb-formats-card">
                <h3 className="font-serif text-2xl text-emerald">{t.tb.formatsTitle}</h3>
                <div className="mt-6 flex flex-wrap gap-3">
                  {t.tb.formatsList.map((f) => (
                    <span key={f} className="rounded-full border border-emerald/20 bg-ivory px-4 py-2 text-sm text-emerald">{f}</span>
                  ))}
                </div>
                <h4 className="mt-10 text-xs uppercase tracking-[0.25em] text-emerald/50">{t.tb.sizesTitle}</h4>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {t.tb.sizes.map((s) => (
                    <div key={s.label} className="border border-emerald/10 rounded-sm px-4 py-3 bg-ivory-soft" data-testid={`tb-size-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <p className="font-serif text-2xl text-terracotta-dark">{s.range}</p>
                      <p className="text-xs text-emerald-ink/60 mt-1">{s.label}</p>
                    </div>
                  ))}
                </div>
                <p data-testid="tb-sizes-note" className="mt-6 text-xs md:text-sm text-emerald-ink/60 leading-relaxed italic">{t.tb.sizesNote}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-emerald grain relative" data-testid="tb-languages">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Kicker light>{t.tb.langsKicker}</Kicker>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-ivory tracking-tight flex items-center gap-4">
              <Languages size={36} className="text-terracotta" strokeWidth={1.5} />
              {t.tb.langsTitle}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Reveal delay={0.1}>
              <div className="border border-ivory/15 rounded-sm p-8 md:p-10 bg-emerald-deep/40" data-testid="tb-langs-cooking">
                <h3 className="font-serif text-2xl text-terracotta italic">{t.tb.cookingLabel}</h3>
                <p className="mt-4 text-ivory text-lg md:text-xl font-light tracking-wide">{t.tb.cookingLangs}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="border border-ivory/15 rounded-sm p-8 md:p-10 bg-emerald-deep/40" data-testid="tb-langs-wine">
                <h3 className="font-serif text-2xl text-terracotta italic">{t.tb.wineLabel}</h3>
                <p className="mt-4 text-ivory text-lg md:text-xl font-light tracking-wide">{t.tb.wineLangs}</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 text-xs uppercase tracking-[0.25em] text-ivory/50">{t.tb.langsNote}</p>
          </Reveal>
        </div>
      </section>

      <Banner title={t.tb.bannerTitle} text={t.tb.bannerText} cta={t.tb.bannerCta} />
    </div>
  );
}
